import React from 'react';

import axios from '../../utils/axiosConfig';
import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import usePagination from '../../components/Pagination/usePagination';

import styles from './OTSettings.module.css';

// OT Settings — manage the approved OT command baseline. The appliance raises an
// alert for any command it sees on a protocol that has a baseline here but does
// not match an approved entry (and for write-style commands where no baseline
// exists). Per-company: each company owns its baseline and an appliance receives
// only its company's; provider staff pick the company, a Company Admin is pinned
// to its own.

const OTSettingsScreen = () => {
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [protocols, setProtocols] = React.useState(['modbus', 'dnp3', 'bacnet', 's7comm']);
  const [msg, setMsg] = React.useState('');
  // Provider staff manage every company's baseline (and must pick which company
  // each command belongs to); a Company Admin is pinned to its own, so no picker.
  const [isProvider, setIsProvider] = React.useState(false);
  const [companies, setCompanies] = React.useState([]);
  const [form, setForm] = React.useState({ protocol: 'modbus', command: '', sender_ip: '', sender_mac: '', responder_ip: '', note: '', company_id: '' });

  const applyData = React.useCallback((data) => {
    setRows(Array.isArray(data.CommandsRows) ? data.CommandsRows : []);
    if (Array.isArray(data.protocols) && data.protocols.length) setProtocols(data.protocols);
    setIsProvider(Boolean(data.isProvider));
    const cos = Array.isArray(data.companies) ? data.companies : [];
    setCompanies(cos);
    if (data.isProvider && cos.length) {
      setForm((f) => (f.company_id ? f : { ...f, company_id: String(cos[0][0]) }));
    }
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/otapproved');
      if (data && data.status) applyData(data);
    } catch (e) { /* leave prior */ }
    setLoading(false);
  }, [applyData]);

  React.useEffect(() => { load(); }, [load]);

  const post = async (body) => {
    setMsg('');
    try {
      const { data } = await axios.post('/api/otapproved', body,
        { headers: { 'Content-Type': 'application/json' } });
      if (data && data.status) {
        applyData(data);
        return true;
      }
      if (data && data.msg) setMsg(data.msg);
    } catch (e) { setMsg('Request failed'); }
    return false;
  };

  const add = async () => {
    if (!form.command.trim()) { setMsg('Enter a command or function code'); return; }
    if (isProvider && !form.company_id) { setMsg('Select a company'); return; }
    const ok = await post({ btn: 'AddCommand', ...form });
    if (ok) setForm({ ...form, command: '', sender_ip: '', sender_mac: '', responder_ip: '', note: '' });
  };

  const remove = (id) => post({ btn: 'DeleteCommand', id });

  const { pageRows, pageNumber, totalRows, pageLimit, handlePageChange } = usePagination(rows);

  return (
    <Content currentMenu='OT' headerTitle='OT Settings'>
      <div className={styles.intro}>
        <strong>Approved OT commands.</strong> The appliance watches Modbus, DNP3,
        BACnet and S7comm control traffic and raises an alert when it sees a command
        that isn&apos;t approved here. Add the commands each protocol is expected to
        carry — anything else on that protocol is flagged <em>Unapproved</em>. Leave
        the sender blank to approve a command from any device. Write-style commands
        on a protocol with no baseline are still flagged as <em>Unverified write</em>.
      </div>

      {msg && <div className={styles.msg}>{msg}</div>}

      <div className={styles.add_card}>
        <div className={styles.add_grid}>
          {isProvider && (
            <label className={styles.field}>
              <span>Company</span>
              <select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
                <option value=''>Select a company…</option>
                {companies.map((c) => <option key={c[0]} value={c[0]}>{c[1]}</option>)}
              </select>
            </label>
          )}
          <label className={styles.field}>
            <span>Protocol</span>
            <select value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })}>
              {protocols.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span>Command / Function code</span>
            <input value={form.command} placeholder='e.g. Read Holding Registers'
              onChange={(e) => setForm({ ...form, command: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
          </label>
          <label className={styles.field}>
            <span>Sender IP <em>(optional)</em></span>
            <input value={form.sender_ip} placeholder='any'
              onChange={(e) => setForm({ ...form, sender_ip: e.target.value })} />
          </label>
          <label className={styles.field}>
            <span>Sender MAC <em>(optional)</em></span>
            <input value={form.sender_mac} placeholder='any'
              onChange={(e) => setForm({ ...form, sender_mac: e.target.value })} />
          </label>
          <label className={styles.field}>
            <span>Destination IP <em>(optional)</em></span>
            <input value={form.responder_ip} placeholder='any'
              onChange={(e) => setForm({ ...form, responder_ip: e.target.value })} />
          </label>
          <label className={styles.field}>
            <span>Note <em>(optional)</em></span>
            <input value={form.note} placeholder='why this is approved'
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </label>
          <button className={styles.add_btn} onClick={add}>Approve</button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <Loader />
      ) : (
        <div className={styles.table_wrap}>
          <Table
            tableHead={
              <tr>
                {isProvider && <th>Company</th>}
                <th>Protocol</th><th>Sender</th><th>Destination</th><th>Command</th><th>Note</th><th>Action</th>
              </tr>
            }
            tableRow={
              pageRows.length > 0 ? (
                // Provider rows carry a leading Company cell, so the id sits one
                // column further right:
                // [Company?, Protocol, Sender, Destination, Command, Note, id].
                pageRows.map((r, i) => {
                  const off = isProvider ? 1 : 0;
                  const id = r[off + 5];
                  return (
                    <tr key={id || i}>
                      {isProvider && <td>{r[0]}</td>}
                      <td className={styles.proto}>{r[off]}</td>
                      <td className={styles.mono}>{r[off + 1]}</td>
                      <td className={styles.mono}>{r[off + 2]}</td>
                      <td>{r[off + 3]}</td>
                      <td className={styles.note}>{r[off + 4]}</td>
                      <td><button className={styles.del_btn} onClick={() => remove(id)}>Remove</button></td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={isProvider ? 7 : 6} style={{ textAlign: 'center' }}>
                  No approved commands yet. Until a protocol has a baseline here, only write-style commands are flagged.
                </td></tr>
              )
            }
          />
          <div className='paginationContainer'>
            <Pagination onPageChange={handlePageChange} rowsPerPage={pageLimit}
              totalPageSize={totalRows} pageNumber={pageNumber} />
          </div>
        </div>
      )}
    </Content>
  );
};

export default OTSettingsScreen;
